import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LocationForm, { LocationFormValues } from './LocationForm';
import { useUpdateLocation } from '../../hooks/useUpdateLocation';
import { Location } from '../../models/Location';

interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithLocation: (location: Location) => void;
}

const EditLocationModal = forwardRef<ExtendedModalCenterRef, object>((_, ref) => {
  const { t } = useTranslation('locationsModule');
  const { toast } = useToast();
  const updateLocationMutation = useUpdateLocation();

  const internalModalRef = useRef<ModalCenterRef>(null);
  const [location, setLocation] = useState<Location | null>(null);

  const handleCancel = () => {
    internalModalRef.current?.closeModal();
    resetForm();
  };

  const handleSubmit = async (values: LocationFormValues) => {
    try {
      if (!location || !values.code || !values.type_id) {
        toast({
          title: t('errors.generic'),
          description: t('modal.edit.error_required'),
          variant: 'destructive',
        });
        return;
      }

      await updateLocationMutation.mutateAsync({
        id: location.id,
        code: values.code.trim(),
        type_id: values.type_id,
        rack_id: values.rack_id === -1 ? null : values.rack_id,
        shelf: values.shelf,
        slot: values.slot,
      });

      toast({
        title: t('modal.edit.success_title'),
        description: t('modal.edit.success_description'),
      });

      internalModalRef.current?.closeModal();
    } catch (error: any) {
      toast({
        title: t('modal.edit.error_title'),
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setLocation(null);
  };

  const openWithLocation = useCallback((locationData: Location) => {
    setLocation(locationData);
    internalModalRef.current?.openModal();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
      openWithLocation,
    }),
    [openWithLocation],
  );

  const defaultValues = location
    ? {
        code: location.code,
        type_id: location.type_id,
        warehouse_id: 1,
        rack_id: location.rack_id,
        shelf: location.shelf,
        slot: location.slot,
      }
    : undefined;

  return (
    <ModalCenter
      ref={internalModalRef}
      title={t('modal.edit.title')}
      type="default"
      width="lg"
      className="h-[calc(100%-30px)] sm:h-auto"
    >
      <LocationForm
        defaultValues={defaultValues}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        submitLabel={t('button.update')}
        isSubmitting={updateLocationMutation.isPending}
      />
    </ModalCenter>
  );
});

EditLocationModal.displayName = 'EditLocationModal';

export default EditLocationModal;
